import { generateLookupKey } from 'common/visualizations/helpers/TableDataExportTotalHelpers';
import { type ExportTotalsInfo } from '../types';
import { type ProcessCellForExportParams, type IRowNode, type CellClassParams } from 'ag-grid-community';
import { constructPivotGrandTotal, isPivotInHierarchy } from 'common/visualizations/helpers/PivotHelpers';
import { PIVOT_SEPARATOR } from '../Constants';

/**
 * Gets custom subtotal value using optimized lookup structure
 *
 * @param node - AG Grid row node containing grouping information
 * @param columnId - The column field name to lookup
 * @param totalsInfo - Totals data containing lookup maps
 * @returns The custom subtotal value or null if not found
 */
export function getCustomSubtotal(
  columnId: string,
  totalsInfo: ExportTotalsInfo | null,
  params?: ProcessCellForExportParams | CellClassParams,
): number | null {
  const node = params?.node;
  if (!node?.group || !totalsInfo?.subtotalLookup || !totalsInfo?.levelMetadata) {
    return null;
  }
  const isPivot = params?.context?.hierarchyConfig
    ? isPivotInHierarchy(params.context.hierarchyConfig)
    : false;
  const pivotKeys = params?.column?.getColDef().pivotKeys;
  if (isPivot && node && pivotKeys) {
    const pivotValue = getPivotSubtotalValue(node, columnId, totalsInfo, pivotKeys);
    if (pivotValue !== null) {
      return pivotValue;
    }
  }

  const level = node.level ?? -1;
  const metadata = totalsInfo.levelMetadata.get(level);
  if (!metadata) {
    return null;
  }

  // Build group path by traversing up the node hierarchy
  const groupPath: Record<string, string> = {};
  let currentNode: IRowNode | null = node;

  while (currentNode && currentNode.level >= 0) {
    const field = currentNode.rowGroupColumn?.getColDef()?.field;
    if (field && currentNode.key !== undefined) {
      const keyValue =
        typeof currentNode.key === 'string' ? currentNode.key : String(currentNode.key ?? 'null');
      groupPath[field] = keyValue;
    }
    currentNode = currentNode.parent;
  }

  // Extract group keys in the correct order based on metadata
  const groupKeys = metadata.groupColumns.map((col: string) => {
    return groupPath[col] ?? 'null';
  });

  const key = generateLookupKey(level, groupKeys, columnId);
  return totalsInfo.subtotalLookup.get(key) ?? null;
}

/**
 * Gets custom grand total value for a specific column
 *
 * @param columnId - The column field name to lookup
 * @param totalsInfo - Totals data containing grand totals
 * @returns The custom grand total value or null if not found
 */
export function getCustomGrandTotal(
  columnId: string,
  totalsInfo: ExportTotalsInfo | null,
  params: ProcessCellForExportParams,
): number | null {
  if (!totalsInfo?.grandTotals || !columnId) {
    return null;
  }

  const colDef = params.column.getColDef();
  const isPivotColumn = colDef?.pivotValueColumn !== undefined;

  if (isPivotColumn && params.context.hierarchyConfig) {
    if (!Array.isArray(totalsInfo.grandTotals)) {
      return totalsInfo.grandTotals[columnId] ?? null;
    }
    const pivotGrandTotals = constructPivotGrandTotal(totalsInfo.grandTotals, params.context.hierarchyConfig);

    if (pivotGrandTotals.length === 0) {
      return null;
    }
    const pivotKeys = colDef?.pivotKeys || [];
    const pivotValueColumnColId = colDef?.pivotValueColumn?.getColId();
    if (!pivotValueColumnColId) {
      return null;
    }
    const pivotColumnId = `${pivotKeys.join(PIVOT_SEPARATOR)}${PIVOT_SEPARATOR}${pivotValueColumnColId}`;
    const value = pivotGrandTotals[0][pivotColumnId];
    if (value !== undefined) {
      const numValue = Number(value);
      return isNaN(numValue) ? null : numValue;
    }
    return null;
  }

  // For non-pivot columns, use the direct lookup
  if (Array.isArray(totalsInfo.grandTotals)) {
    const value = totalsInfo.grandTotals[0]?.[columnId];
    if (value === undefined) {
      return null;
    }
    const numValue = Number(value);
    return isNaN(numValue) ? null : numValue;
  }
  return totalsInfo.grandTotals[columnId] ?? null;
}

/**
 * Gets pivot subtotal value for a specific node and column
 */
function getPivotSubtotalValue(
  node: IRowNode,
  columnId: string,
  totalsInfo: ExportTotalsInfo,
  pivotKeys: string[],
): number | null {
  if (pivotKeys && pivotKeys.length > 0) {
    const pivotKey = pivotKeys.join(PIVOT_SEPARATOR);
    const matchingKey = `${pivotKey}${PIVOT_SEPARATOR}${columnId}`;

    // Try to find the value in the subtotal lookup using the level and pivot key
    const level = node.level ?? -1;
    const metadata = totalsInfo.levelMetadata.get(level);

    if (metadata) {
      // Extract group keys similar to the non-pivot case
      const groupPath: Record<string, string> = {};
      let currentNode: IRowNode | null = node;

      while (currentNode && currentNode.level >= 0) {
        const field = currentNode.rowGroupColumn?.getColDef()?.field;
        if (field && currentNode.key !== undefined) {
          const keyValue =
            typeof currentNode.key === 'string' ? currentNode.key : String(currentNode.key ?? 'null');
          groupPath[field] = keyValue;
        }
        currentNode = currentNode.parent;
      }

      // Extract group keys in the correct order
      const groupKeys = metadata.groupColumns.map((col: string) => {
        return groupPath[col] ?? 'null';
      });

      // For pivot tables, we need to include the pivot column in the lookup key
      const pivotLookupKey = generateLookupKey(level, groupKeys, matchingKey);
      const value = totalsInfo.subtotalLookup.get(pivotLookupKey);
      if (value !== undefined) {
        return value;
      }
    }
  }

  return null;
}
